import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import {
  TypographyCaption,
  TypographyCaptionBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { RpaSkillWorkflowDisplay } from '../lib/read-rpa-skill-workflow';
import {
  RecordedWorkflowBulletList,
  RecordedWorkflowParameterItem,
  RecordedWorkflowSection,
  RecordedWorkflowSlugChip,
  RecordedWorkflowStepItem,
} from './ComputerUseSkillWorkflowPlan.parts';

const recordedWorkflowMessages = defineMessages({
  label: {
    id: 'DUYO1L2gpH',
    defaultMessage: 'Recorded routine',
  },
  recordedStepsLabel: {
    id: 'kKdQSabl6j',
    defaultMessage: 'Recorded steps',
  },
  stepsGuidance: {
    defaultMessage:
      'These steps are recorded guidance — Heidi may adapt them to what’s on screen.',
    id: '/GYBWKUOZX',
    description:
      'Caption above the recorded workflow steps on a computer-use task card, clarifying the steps come from the saved recording and the live run may adapt them to the current screen',
  },
  inputsLabel: {
    id: 'wS/QCKGNeY',
    defaultMessage: 'Inputs',
  },
  prerequisitesLabel: {
    id: 'FtNf4MPnP0',
    defaultMessage: 'Before you run this',
  },
  notesLabel: {
    id: '7+DomhYCSv',
    defaultMessage: 'Notes',
  },
  stepCount: {
    id: 'vAKAnIbJ4M',
    defaultMessage: '{count, plural, one {# step} other {# steps}}',
  },
});

export const ComputerUseRecordedWorkflowPlan = ({
  workflow,
  nested = false,
  defaultExpanded = false,
}: {
  workflow: RpaSkillWorkflowDisplay | undefined;
  nested?: boolean;
  /**
   * The recorded workflow is expanded while awaiting computer-use approval
   * so the clinician can review it, and collapsed by default once execution
   * starts and on terminal historical cards.
   */
  defaultExpanded?: boolean;
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Mid-task approval keeps this card mounted, so re-sync when the
  // awaiting→running (or reverse) transition flips the prop. Manual
  // toggles between transitions still win until the next flip.
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);
  if (!workflow) return null;

  const { presentation } = workflow;
  const parameters =
    presentation.parameters?.filter((parameter) => parameter.name.trim()) ?? [];

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className={cn(
        'mt-2 flex flex-col gap-3 rounded-md text-xs leading-snug text-text-secondary',
        nested ? 'text-text-secondary' : 'bg-background-secondary p-3'
      )}
      data-testid="cu-recorded-workflow"
    >
      <CollapsibleTrigger
        type="button"
        className="flex w-full flex-wrap items-center justify-between gap-2 rounded-sm text-start"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          {expanded ? (
            <ChevronDown className="size-3 shrink-0 text-text-tertiary" />
          ) : (
            <ChevronRight className="size-3 shrink-0 text-text-tertiary rtl:-scale-x-100" />
          )}
          <span
            className={cn(
              typographyVariant({ variant: 'captionBold' }),
              'text-text-secondary'
            )}
          >
            {intl.formatMessage(recordedWorkflowMessages.label)}
          </span>
          {workflow.slug && <RecordedWorkflowSlugChip slug={workflow.slug} />}
        </span>
        <span className="rounded bg-background-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
          {intl.formatMessage(recordedWorkflowMessages.stepCount, {
            count: presentation.steps.length,
          })}
        </span>
      </CollapsibleTrigger>

      <div className="flex flex-col gap-0.5">
        <TypographyCaptionBold className="break-words text-text-primary [overflow-wrap:anywhere]">
          {presentation.title}
        </TypographyCaptionBold>
        {presentation.description && (
          <TypographyCaption className="block break-words text-text-secondary [overflow-wrap:anywhere]">
            {presentation.description}
          </TypographyCaption>
        )}
      </div>

      <CollapsibleContent className="flex flex-col gap-3">
        {parameters.length > 0 && (
          <RecordedWorkflowSection
            label={intl.formatMessage(recordedWorkflowMessages.inputsLabel)}
          >
            <ul className="flex flex-wrap gap-x-3 gap-y-1.5">
              {parameters.map((parameter) => (
                <RecordedWorkflowParameterItem
                  key={parameter.name}
                  parameter={parameter}
                  value={workflow.inputValues[parameter.name]}
                />
              ))}
            </ul>
          </RecordedWorkflowSection>
        )}

        {(presentation.prerequisites?.length ?? 0) > 0 && (
          <RecordedWorkflowSection
            label={intl.formatMessage(recordedWorkflowMessages.prerequisitesLabel)}
          >
            <RecordedWorkflowBulletList items={presentation.prerequisites ?? []} />
          </RecordedWorkflowSection>
        )}

        <RecordedWorkflowSection
          label={intl.formatMessage(recordedWorkflowMessages.recordedStepsLabel)}
        >
          <TypographyCaption className="text-text-tertiary">
            {intl.formatMessage(recordedWorkflowMessages.stepsGuidance)}
          </TypographyCaption>
          <ol className="flex list-none flex-col gap-2">
            {presentation.steps.map((step) => (
              <RecordedWorkflowStepItem
                key={step.id}
                step={step}
                inputValues={workflow.inputValues}
              />
            ))}
          </ol>
        </RecordedWorkflowSection>

        {(presentation.notes?.length ?? 0) > 0 && (
          <RecordedWorkflowSection
            label={intl.formatMessage(recordedWorkflowMessages.notesLabel)}
          >
            <RecordedWorkflowBulletList items={presentation.notes ?? []} />
          </RecordedWorkflowSection>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
